import { Box } from '@mui/material';
import { useCallback } from 'react';
import {
  DataItemCellSelect,
  type SelectedCellParams,
  type SelectedColumnParams,
} from '~/components/inputs';
import { useStudio, type StudioItem } from '../../context';
import { transformText } from '../lib';

export interface StudioSettingsDataTabProps {
  selectedItem: Extract<StudioItem, { type: 'text' }>;
}

const isColumn = (
  selected: SelectedCellParams | SelectedColumnParams,
): selected is SelectedColumnParams => 'dataSourceId' in selected;

export const StudioSettingsDataTab = ({ selectedItem }: StudioSettingsDataTabProps) => {
  const disabled = selectedItem.locked;
  const {
    dataItemCellSelectOpen,
    previewDeviceId,
    previewTextFor,
    setDataItemCellSelectOpen,
    setPreviewDeviceId,
    updateItems,
  } = useStudio();

  const handleChange = useCallback(
    (selected: SelectedCellParams | SelectedColumnParams | undefined) => {
      // A device-aware item keeps whatever text is on the canvas: it is the placeholder
      // shown on any device with no matching row.
      if (selected && isColumn(selected)) {
        updateItems([
          {
            id: selectedItem.id,
            __dataItemId: undefined,
            __dataItemKey: selected.field,
            __deviceDataSourceId: selected.dataSourceId,
          },
        ]);
        return;
      }

      updateItems([
        {
          id: selectedItem.id,
          __dataItemId: selected ? Number(selected.id) : undefined,
          __dataItemKey: selected ? selected.field : undefined,
          __deviceDataSourceId: undefined,
          ...(selected?.value && {
            text: transformText(selected.value, selectedItem.__textTransform),
          }),
        },
      ]);
    },
    [selectedItem.__textTransform, selectedItem.id, updateItems],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <DataItemCellSelect
        dataItemId={selectedItem.__dataItemId}
        dataItemKey={selectedItem.__dataItemKey}
        deviceDataSourceId={selectedItem.__deviceDataSourceId}
        disabled={disabled}
        onChange={handleChange}
        onPreviewDeviceChange={setPreviewDeviceId}
        open={dataItemCellSelectOpen}
        previewDeviceId={previewDeviceId}
        previewValue={previewTextFor(selectedItem)}
        setOpen={setDataItemCellSelectOpen}
      />
    </Box>
  );
};
